import { useMemo } from 'react';
import { spacing } from 'ui/theme';
import { BaseOption, MultiFilterSelect } from 'ui/selects';
import { sortObjectListByKey } from 'utils/arrayHelpers';

import { AggregateEventInfo, EventName } from '../../../domain';
import { useEventFilterContext } from '../contexts/filter';
import { useSummaryQueryContext } from '../contexts/query';
import { useEventSelectionContext } from '../contexts/selection';
import { getEventLabel } from '../../utils';
import { EventDatePicker } from './EventDatePicker';
import { FilterBanner } from './styled';

const getFilterOptions = (events: AggregateEventInfo[]) => {
  const options: BaseOption<string>[] = events?.map((event) => ({
    categoryId: event.eventType,
    label: getEventLabel(event),
    id: event.eventName,
    value: event.eventName,
  }));
  const optionsSortedByLabel = sortObjectListByKey(options, 'label'); // sort labels alphabetically
  const optionsSortedByCategory = sortObjectListByKey(optionsSortedByLabel, 'categoryId', true); // promote system events over custom ones

  return optionsSortedByCategory;
};

/**
 * Customize the summarizing text that appears at the top of the dropdown
 *
 * selectedEvents is intentionally independent of date filtering, so that
 * when a user resets date filters to include previously selected events,
 * those events remain as selected and are subsequently displayed.
 *
 * However,the text here is meant to show a count of how many of the displayed
 * options are selected, and we therefore need to take the intersection between
 * selectedEvents and the displayed events to get the correct count.
 */
const getDropdownLabel = (options: BaseOption<string>[], selection: EventName[]) => {
  const eventOptionValues = options.map((option) => option.value);
  const totalEventOptions = options.length;

  const displayedSelectedEvents = selection.filter((event) => eventOptionValues.includes(event));
  const [firstSelectedEvent] = displayedSelectedEvents;

  let dropdownHeaderText;
  switch (displayedSelectedEvents.length) {
    case 1:
      dropdownHeaderText =
        totalEventOptions === 1 ? 'all events' : getEventLabel({ eventType: firstSelectedEvent });
      break;
    case totalEventOptions:
      dropdownHeaderText = 'all events';
      break;
    default:
      dropdownHeaderText = `${displayedSelectedEvents.length} events`;
  }

  return dropdownHeaderText;
};

export const EventFilterBanner = () => {
  const { events } = useSummaryQueryContext();
  const { isFiltering } = useEventFilterContext();
  const { types, setTypes } = useEventSelectionContext();

  const options = useMemo(() => getFilterOptions(events), [events]);
  const dropdownHeaderText = useMemo(() => getDropdownLabel(options, types), [options, types]);

  const isDisabled = events.length === 0 && !isFiltering;

  return (
    <FilterBanner data-test="filter-banner">
      <div>
        Showing{' '}
        <span>
          <MultiFilterSelect
            id="event-options"
            emptyStateText="No events associated with this user"
            placeholder="0 events"
            inputContent={{ filterPlaceholder: 'Search events' }}
            categoryLabels={{ System: 'System', Custom: 'Custom' }}
            isExpandable
            options={options}
            defaultValues={types}
            onSelect={setTypes}
            optionWrapperWidth={spacing.x24}
            selectValueDisplayText={dropdownHeaderText}
            isDisabled={isDisabled}
          />
        </span>{' '}
        recorded <EventDatePicker isDisabled={isDisabled} />
      </div>
    </FilterBanner>
  );
};
